import { cx } from "@emotion/css";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import MapGl, { type MapRef, Marker, NavigationControl } from "react-map-gl/maplibre";
import { switzerlandBoundsWithPadding, type WGS84Coordinates } from "../services/coords";
import { ResetMapControl } from "./ResetMapControl";

type Props = {
  wgs84Coordinates: WGS84Coordinates;
  swisstopoUrl?: string;
  showControls?: boolean;
};

export default function CampsiteMap({ wgs84Coordinates, swisstopoUrl, showControls = true }: Props) {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const { latitude, longitude } = wgs84Coordinates;
  const { t } = useTranslation();
  const ref = useRef<MapRef>(null);
  const map = ref.current?.getMap();
  const handleResetMap = useCallback(() => {
    if (!map) return;
    map.flyTo({
      center: [longitude, latitude],
      zoom: 15,
      essential: true,
      duration: 100,
    });
  }, [map, latitude, longitude]);

  if (!wgs84Coordinates) return;
  return (
    <div className={cx({ loaded: isLoaded }, "position-relative")}>
      <MapGl
        ref={ref}
        initialViewState={{
          longitude: longitude,
          latitude: latitude,
          zoom: 15,
        }}
        onLoad={(_e) => setIsLoaded(true)}
        attributionControl={false}
        renderWorldCopies={false}
        scrollZoom={false}
        touchZoom={true}
        cooperativeGestures={true}
        maxBounds={switzerlandBoundsWithPadding}
        style={{ height: "30rem", width: "100%" }}
        mapStyle="https://vectortiles.geo.admin.ch/styles/ch.swisstopo.basemap.vt/style.json"
      >
        {showControls && <NavigationControl showCompass={false} />}
        {showControls && <ResetMapControl onClick={handleResetMap} />}

        <Marker longitude={longitude} latitude={latitude} />
      </MapGl>
      {swisstopoUrl && (
        <a
          href={swisstopoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-sm btn-light border bg-white position-absolute bottom-0 end-0 m-2"
          style={{ zIndex: 1 }}
        >
          {t("public.campsites.campsite.open_swisstopo")}
        </a>
      )}
    </div>
  );
}
